/// <amd-module name="Repos/Views/Settings/Branches/Components/BranchesTree/BranchesTree" />
import { EnhancedGitRef } from "Data/EnhancedGitRef";
import * as React from "react";
import { branchNameToRef } from "Repos/Common/Util/Ref";
import { AsyncSparseTreeLoadingCell } from "Repos/SparseTree/AsyncSparseTreeLoadingCell";
import { SparseTreeFolder, SparseTreeItem, SparseTreeNode } from "Repos/SparseTree/SparseTreeProvider.Types";
import { ISettingsViewService, SettingsQueryStringParams } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { IObservableLikeValue, ObservableLike } from "VSS/Core/Observable";
import { useComponentContext } from "VSS/Platform/Layout";
import { renderColumns, TableColumnLayout } from "VSSUI/Table";
import { ITreeColumn, ITreeRowDetails, Tree } from "VSSUI/TreeEx";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import * as BranchColumns from "../Columns/BranchColumns";
import { ShareFocusTreeRow } from "../SharedFocusTreeRow/ShareFocusTreeRow";
import { IBranchesTreeProps } from "./BranchesTree.Props";

function getColumns(props: IBranchesTreeProps): ITreeColumn<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>>[] {
    return [BranchColumns.NameColumn(props.branchProvider.pathHasPolicy)];
}

export const BranchesTree: React.FunctionComponent<IBranchesTreeProps> = props => {
    const { pageContext } = useComponentContext();
    const { branchProvider, onRowActivate } = props;
    const [columns] = React.useState(() => getColumns(props));
    const settingsSvc = pageContext.getService<ISettingsViewService>("SettingsViewService");
    const routeValues = settingsSvc.getUrlParameters();
    const repositoryId = routeValues[SettingsQueryStringParams.repositoryId];
    const tabId = routeValues[SettingsQueryStringParams.tabId];

    const onClick = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>,
        row: ITreeItemEx<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>>
    ) => {
        if (!event.shiftKey && !event.ctrlKey && !event.defaultPrevented) {
            const sparseNode = ObservableLike.getValue(row.underlyingItem.data);
            if (sparseNode.kind === "item") {
                onRowActivate(sparseNode.data);
            } else {
                const item = { ref: branchNameToRef(sparseNode.absoluteIdentifier) + "/", isFolder: true };
                onRowActivate(item);
            }

            event.preventDefault();
        }
    };

    const onToggle = (
        event: React.SyntheticEvent<HTMLElement, Event>,
        treeItem: ITreeItemEx<IObservableLikeValue<SparseTreeFolder | SparseTreeItem<EnhancedGitRef> | undefined>>
    ) => {
        branchProvider.baseProvider.toggle(event, treeItem);
        event.preventDefault();
    };

    return (
        <Tree
            ariaLabel={props.ariaLabel}
            renderRow={(
                rowIndex: number,
                item: ITreeItemEx<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>>,
                details: ITreeRowDetails<IObservableLikeValue<SparseTreeNode<EnhancedGitRef> | undefined>>
            ) => {
                const sparseNode = ObservableLike.getValue(item.underlyingItem.data);
                const linkRefAppend = (sparseNode.kind === "folder" && "/") || "";
                return (
                    <ShareFocusTreeRow
                        index={rowIndex}
                        details={details}
                        className={details.className}
                        indexOffset={props.indexOffset}
                        focusZoneId={props.focusZoneContext.focuszoneId}
                        linkProps={{
                            href: settingsSvc.getUrl(repositoryId, branchNameToRef(sparseNode.absoluteIdentifier) + linkRefAppend, tabId),
                            onClick: event => onClick(event, item)
                        }}
                    >
                        {renderColumns(rowIndex, columns, item, details)}
                    </ShareFocusTreeRow>
                );
            }}
            itemProvider={branchProvider.baseProvider}
            columns={columns}
            onToggle={onToggle}
            showHeader={false}
            selection={props.selection}
            renderLoadingRow={(rowIndex: number, rowDetails: ITreeRowDetails<IObservableLikeValue<SparseTreeNode<EnhancedGitRef> | undefined>>) => {
                return (
                    <ShareFocusTreeRow
                        key={rowIndex}
                        className="bolt-list-row-loading"
                        details={rowDetails}
                        index={rowIndex}
                        indexOffset={props.indexOffset}
                        focusZoneId={props.focusZoneContext.focuszoneId}
                        linkProps={{}}
                    >
                        <AsyncSparseTreeLoadingCell
                            columnIndex={0}
                            columnLayout={TableColumnLayout.singleLinePrefix}
                            fetchData={() =>
                                rowDetails.data.parentItem &&
                                ObservableLike.getValue(rowDetails.data.parentItem.underlyingItem.data) &&
                                // Made safe by the type check the line above
                                branchProvider.baseProvider.loadNextPage(
                                    rowDetails.data.parentItem as ITreeItemEx<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>>
                                )
                            }
                            treeItem={rowDetails.data}
                        />
                    </ShareFocusTreeRow>
                );
            }}
            virtualize={false}
        />
    );
};
